import { OpenPanel } from "@openpanel/web";
import { useEffect } from "react";
import type { TrackPayload } from "./events.ts";

export type { TrackPayload } from "./events.ts";

export type BrowserAnalyticsConfig = {
  clientId: string;
  /**
   * Self-hosted: use your public API URL including the `/api` path, e.g.
   * `https://analytics.example.com/api` (see OpenPanel self-hosting docs).
   * The SDK posts to `{apiUrl}/track`.
   */
  apiUrl?: string;
  /** No SDK instance; all calls no-op */
  disabled?: boolean;
  /** Log to console only (no network) */
  dryRun?: boolean;
  trackScreenViews?: boolean;
  trackOutgoingLinks?: boolean;
  trackAttributes?: boolean;
  /** Forward OpenPanel SDK debug logs (`[OpenPanel.dev]`). */
  debug?: boolean;
  globalProperties?: Record<string, unknown>;
};

let panel: OpenPanel | null = null;
let dryRunMode = false;
let initialized = false;

export function initBrowserAnalytics(config: BrowserAnalyticsConfig): void {
  if (initialized) {
    return;
  }

  const {
    clientId,
    apiUrl,
    disabled = false,
    dryRun = false,
    trackScreenViews = true,
    trackOutgoingLinks = true,
    trackAttributes = true,
    debug = false,
    globalProperties,
  } = config;

  dryRunMode = dryRun;

  if (disabled || !clientId) {
    initialized = true;
    panel = null;
    return;
  }

  if (dryRun) {
    initialized = true;
    panel = null;
    return;
  }

  panel = new OpenPanel({
    clientId,
    apiUrl,
    trackScreenViews,
    trackOutgoingLinks,
    trackAttributes,
    disabled: false,
    waitForProfile: true,
    debug,
  });

  if (globalProperties && Object.keys(globalProperties).length > 0) {
    try {
      panel.setGlobalProperties(globalProperties);
    } catch {
      // ignore
    }
  }

  initialized = true;
}

export function track(options: TrackPayload): void {
  const { event, channel, ...rest } = options;

  if (dryRunMode) {
    console.log("[@graftus/events]", "track", { event, channel, ...rest });
    return;
  }

  if (!panel) {
    return;
  }

  try {
    panel.track(event, { channel, ...rest });
  } catch (e) {
    console.warn("[@graftus/events] track failed", e);
  }
}

export function setBrowserGlobalProperties(
  properties: Record<string, unknown>,
): void {
  if (!panel) {
    return;
  }
  try {
    panel.setGlobalProperties(properties);
  } catch {
    // ignore
  }
}

export type IdentifyBrowserPayload = {
  profileId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  properties?: Record<string, unknown>;
};

export function identifyBrowserUser(payload: IdentifyBrowserPayload): void {
  if (dryRunMode) {
    console.log("[@graftus/events]", "identify", payload);
    return;
  }
  if (!panel) {
    return;
  }
  try {
    panel.identify({
      profileId: payload.profileId,
      email: payload.email,
      firstName: payload.firstName,
      lastName: payload.lastName,
      properties: payload.properties,
    });
  } catch (e) {
    console.warn("[@graftus/events] identify failed", e);
  }
}

export function clearBrowserUser(): void {
  if (dryRunMode) {
    console.log("[@graftus/events]", "clear");
    return;
  }
  if (!panel) {
    return;
  }
  try {
    panel.clear();
  } catch (e) {
    console.warn("[@graftus/events] clear failed", e);
  }
}

export type CompanyGroupPayload = {
  id: string;
  name: string;
  properties?: Record<string, unknown>;
};

export function syncBrowserCompanyGroup(payload: CompanyGroupPayload): void {
  if (dryRunMode) {
    console.log("[@graftus/events]", "syncCompanyGroup", payload);
    return;
  }
  if (!panel) {
    return;
  }
  try {
    panel.upsertGroup({
      id: payload.id,
      type: "company",
      name: payload.name,
      properties: payload.properties,
    });
    panel.setGroup(payload.id);
  } catch (e) {
    console.warn("[@graftus/events] syncCompanyGroup failed", e);
  }
}

/** Mount once after `initBrowserAnalytics` to refresh global properties if needed. */
export function Analytics(props: {
  globalProperties?: Record<string, unknown>;
}): null {
  const { globalProperties } = props;
  useEffect(() => {
    if (globalProperties && Object.keys(globalProperties).length > 0) {
      setBrowserGlobalProperties(globalProperties);
    }
  }, [globalProperties]);
  return null;
}

export function getBrowserOpenPanel(): OpenPanel | null {
  return panel;
}
